import { useEffect, useMemo, useState } from 'react';
import { validateCoupon } from '../../services/coupons';
import header11 from '../../assets/images/foundation-pack-header-11.png';
import header12 from '../../assets/images/foundation-pack-header-12.png';

const TRACK_META = {
  11: {
    title: '11th - Foundation Track',
    headerImage: header11,
    closeClass: 'bg-[#1A87CC]',
  },
  12: {
    title: '12th - Exam Track',
    headerImage: header12,
    closeClass: 'bg-[#E07A2A]',
  },
};

function resolveTrackMeta(plans) {
  const sample = plans?.[0];
  const label = String(sample?.categoryName || sample?.name || '').toLowerCase();
  if (label.includes('12')) return TRACK_META[12];
  if (label.includes('11')) return TRACK_META[11];
  return TRACK_META[11];
}

function formatValidity(plan) {
  const duration = String(plan?.duration || '').trim();
  if (!duration) return 'Flexible access';
  if (/until exam/i.test(duration)) return 'Valid until Exam';
  if (/^\d+\s*month/i.test(duration)) return `Valid for ${duration}`;
  if (/^\d+\s*day/i.test(duration)) return `Valid for ${duration}`;
  return `Valid for ${duration}`;
}

function formatPlanPrice(plan) {
  return plan?.priceFormatted || `₹${plan?.price ?? 0}`;
}

function formatAmount(value) {
  if (value == null || Number.isNaN(Number(value))) return '₹0';
  return `₹${Math.round(Number(value))}`;
}

function CheckIcon() {
  return (
    <svg className="h-5 w-5 text-[#2E7D32]" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-7.25 7.25a1 1 0 01-1.414 0l-3-3a1 1 0 011.414-1.414L8.5 11.586l6.543-6.543a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function PlanRadio({ plan, selected, onSelect }) {
  const saveBadge =
    plan.hasDiscount && Number(plan.discountPercentage) > 0
      ? `Save ${Math.round(Number(plan.discountPercentage))}%`
      : null;
  const popularBadge = /popular|most chosen/i.test(String(plan.badge || '')) ? 'Popular' : null;
  const badge = saveBadge || popularBadge;

  return (
    <button
      type="button"
      onClick={() => onSelect(plan.id)}
      className={`relative flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left transition ${
        selected
          ? 'border-2 border-[#9369CC] bg-white shadow-sm'
          : 'border border-transparent bg-[#F3F2F3]'
      }`}
    >
      {badge && (
        <span
          className={`absolute -right-1 -top-2 rounded-md px-2 py-0.5 text-[11px] font-semibold text-white ${
            saveBadge ? 'bg-[#53BD0C]' : 'bg-[#E45A9B]'
          }`}
        >
          {badge}
        </span>
      )}

      <span
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
          selected ? 'border-[#9369CC]' : 'border-[#BDBDBD]'
        }`}
      >
        {selected ? <span className="h-2.5 w-2.5 rounded-full bg-[#9369CC]" /> : null}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block text-sm font-bold text-[#1B1B1B] sm:text-base">{plan.name}</span>
        <span className="mt-0.5 block text-xs text-[#666666] sm:text-sm">{formatValidity(plan)}</span>
      </span>

      <span className="shrink-0 text-base font-bold text-[#1B1B1B] sm:text-lg">{formatPlanPrice(plan)}</span>
    </button>
  );
}

export default function FoundationPackPopup({
  open,
  plans = [],
  selectedPlanId,
  onSelectPlan,
  onClose,
  onProceed,
  paying = false,
  isPlanSubscribed,
}) {
  const track = useMemo(() => resolveTrackMeta(plans), [plans]);
  const selectedPlan = useMemo(
    () => plans.find((plan) => String(plan.id) === String(selectedPlanId)) || plans[0] || null,
    [plans, selectedPlanId]
  );

  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape' && !paying) onClose?.();
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [open, onClose, paying]);

  useEffect(() => {
    if (!open) {
      setCouponInput('');
      setAppliedCoupon(null);
      setCouponError('');
      setCouponLoading(false);
    }
  }, [open]);

  useEffect(() => {
    setAppliedCoupon(null);
    setCouponError('');
  }, [selectedPlanId]);

  const basePrice = Number(selectedPlan?.price ?? selectedPlan?.originalPrice ?? 0);
  const baseFormatted = formatPlanPrice(selectedPlan) || formatAmount(basePrice);

  const payTotal = appliedCoupon?.final_price != null ? Number(appliedCoupon.final_price) : basePrice;
  const payTotalFormatted =
    appliedCoupon?.final_price_formatted || formatAmount(payTotal);
  const strikeFormatted =
    appliedCoupon
      ? appliedCoupon.base_price_formatted || appliedCoupon.original_price_formatted || baseFormatted
      : selectedPlan?.hasDiscount
        ? selectedPlan.originalPriceFormatted || formatAmount(selectedPlan.originalPrice)
        : null;

  const savingText = useMemo(() => {
    if (!appliedCoupon) return '';
    const original = Number(appliedCoupon.base_price ?? appliedCoupon.original_price ?? basePrice);
    const finalPrice = Number(appliedCoupon.final_price ?? payTotal);
    if (!original || finalPrice >= original) return 'Coupon Applied';
    const pct = Math.round(((original - finalPrice) / original) * 100);
    return pct > 0 ? `You're saving ${pct}% on your plan` : 'Coupon Applied';
  }, [appliedCoupon, basePrice, payTotal]);

  const handleApplyCoupon = async () => {
    if (!selectedPlan?.id) return;
    const code = couponInput.trim();
    if (!code) {
      setCouponError('Please enter a coupon code.');
      return;
    }

    setCouponLoading(true);
    setCouponError('');

    try {
      const res = await validateCoupon({ code, planId: selectedPlan.id });
      const planPricing = res?.data?.plan;
      if (!planPricing) {
        throw new Error(res?.message || 'Unable to validate coupon.');
      }

      setAppliedCoupon({
        code,
        ...planPricing,
        coupon: res?.data?.coupon || null,
      });
    } catch (error) {
      setAppliedCoupon(null);
      setCouponError(error?.message || 'Invalid coupon code.');
    } finally {
      setCouponLoading(false);
    }
  };

  const handleClearCoupon = () => {
    setCouponInput('');
    setAppliedCoupon(null);
    setCouponError('');
  };

  const handleProceed = async () => {
    if (!selectedPlan) return;
    await onProceed?.({
      plan: selectedPlan,
      couponCode: appliedCoupon?.code || null,
    });
  };

  if (!open || !selectedPlan) return null;

  const subscribed = Boolean(isPlanSubscribed?.(selectedPlan));

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4 backdrop-blur-[1px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="foundation-pack-title"
      onClick={paying ? undefined : onClose}
    >
      <div
        className="relative w-full max-w-[520px] h-[600px] rounded-[28px] bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="relative overflow-hidden rounded-t-[28px]">
          <img
            src={track.headerImage}
            alt={track.title}
            className="block h-auto w-full object-cover object-top"
          />
          <h2 id="foundation-pack-title" className="sr-only">
            {track.title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={paying}
            aria-label="Close"
            className={`absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-white shadow-md ${track.closeClass} disabled:opacity-60 sm:right-4 sm:top-4`}
          >
            <CloseIcon />
          </button>
        </div>

        <div className="overflow-y-auto px-5 pb-5 pt-2 sm:pb-6" style={{ maxHeight: 'calc(568px - 140px)' }}>
          <div className="space-y-3">
            {plans.map((plan) => (
              <PlanRadio
                key={plan.id}
                plan={plan}
                selected={String(plan.id) === String(selectedPlan.id)}
                onSelect={onSelectPlan}
              />
            ))}
          </div>

          <div className="mt-5">
            <label className="text-sm font-medium text-[#1B1B1B]" htmlFor="coupon-code">
              Apply Coupon
            </label>
            <div className="mt-2 flex gap-2">
              <div className="relative flex-1">
                <input
                  id="coupon-code"
                  type="text"
                  value={couponInput}
                  onChange={(event) => setCouponInput(event.target.value.toUpperCase())}
                  placeholder="Enter coupon code"
                  className="w-full rounded-xl border border-transparent bg-[#251A3380] px-4 py-3 pr-9 text-sm font-medium text-[#fff] outline-none focus:border-[#9369CC] placeholder:text-[#fff]"
                />
                {couponInput ? (
                  <button
                    type="button"
                    onClick={handleClearCoupon}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#fff]"
                    aria-label="Clear coupon"
                  >
                    ×
                  </button>
                ) : null}
              </div>
              <button
                type="button"
                onClick={handleApplyCoupon}
                disabled={couponLoading || paying}
                className="rounded-xl bg-gradient-to-t from-[#6A74E1] to-[#B539FD] px-10 py-3 text-sm font-bold uppercase tracking-wide text-white disabled:opacity-60"
              >
                {couponLoading ? '...' : 'Apply'}
              </button>
            </div>

            {couponError ? <p className="mt-2 text-sm text-red-600">{couponError}</p> : null}

            {appliedCoupon ? (
              <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-2">
                  <CheckIcon />
                  <div>
                    <p className="text-sm font-semibold text-[#2E7D32]">Coupon Applied</p>
                    <p className="text-xs text-[#666666]">{savingText}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-[#6A5AE0] sm:text-lg">
                    Pay Total: <span className="text-lg font-bold sm:text-xl">{payTotalFormatted}</span>
                  </p>
                  {strikeFormatted ? (
                    <p className="text-sm text-[#999999] line-through">{strikeFormatted}</p>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="mt-2 text-right">
                <p className="text-sm font-semibold text-[#6A5AE0] sm:text-lg">
                  Pay Total: <span className="text-lg font-bold sm:text-xl">{payTotalFormatted}</span>
                </p>
                {strikeFormatted ? (
                  <p className="text-sm text-[#999999] line-through">{strikeFormatted}</p>
                ) : null}
              </div>
            )}
          </div>

          <div className="mt-3 flex justify-center">
            <button
              type="button"
              onClick={handleProceed}
              disabled={paying || subscribed}
              className="w-2/3 rounded-full bg-gradient-to-t from-[#6A74E1] to-[#B539FD] px-6 py-2.5 text-base font-bold text-white shadow-md transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {paying ? 'Processing...' : subscribed ? 'Already Subscribed' : 'Proceed To pay'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
